import { useEffect, useState } from 'react';

import { usePrevious } from '../../../../utils/hooks/usePrevious';
import { Menu } from '../../../api/menu/menu.types';
import { Async, AsyncMap, Status } from '../../../types/data.types';

interface UseMenu {
  data: Menu | null;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
}

export const useMenu = (
  loadMenu: (url: string) => void,
  menus: AsyncMap<Menu>,
  menuUrl?: string,
): UseMenu => {
  const previousMenuUrl = usePrevious(menuUrl);

  const getMenuInitialState = (): Async<Menu> => {
    let initialState: Async<Menu> = { status: Status.LOADING };

    if (menuUrl) {
      const menuFromRedux = menus[menuUrl];
      if (menuFromRedux?.status === Status.SUCCESS) {
        initialState = menuFromRedux;
      }
    }

    return initialState;
  };

  const [menu, setMenu] = useState<Async<Menu>>(() => {
    const initialState = getMenuInitialState();
    return initialState;
  });

  const isError = menu.status === Status.ERROR;
  const isLoading = menu.status === Status.LOADING;
  const isSuccess = menu.status === Status.SUCCESS;
  const data = isSuccess ? menu.data : null;

  useEffect(() => {
    const isMenuNotLoaded = !isSuccess && menuUrl;
    const hasMenuUpdated =
      previousMenuUrl && menuUrl && previousMenuUrl !== menuUrl;

    if (isMenuNotLoaded || hasMenuUpdated) {
      // * Redux state - menu.menus[menuUrl]
      // - set menu indexed by menuUrl
      loadMenu(menuUrl);
    }
  }, [menuUrl]);

  useEffect(() => {
    const menuFromRedux = menus[menuUrl];
    if (menuFromRedux) {
      setMenu(menuFromRedux);
    }
  }, [menus[menuUrl]?.status, menuUrl]);

  return {
    data,
    isError,
    isLoading,
    isSuccess,
  };
};
